/**
 * Auto-response rule management for PTY sessions.
 *
 * Contains logic for pushing default auto-response rules per agent type
 * and handling Gemini authentication flow.
 *
 * @module services/pty-auto-response
 */

import type { IAgentRuntime } from "@elizaos/core";
import type {
  AutoResponseRule,
  BunCompatiblePTYManager,
  PTYManager,
} from "pty-manager";

export interface AutoResponseContext {
  manager: PTYManager | BunCompatiblePTYManager;
  usingBunWorker: boolean;
  runtime: IAgentRuntime;
  log: (msg: string) => void;
}

/**
 * Push session-specific auto-response rules that depend on runtime config.
 * Trust prompts, update notices, and other static rules are handled by
 * adapter built-in rules (coding-agent-adapters). This only pushes rules
 * that need runtime values (e.g. API keys).
 */
export async function pushDefaultRules(
  ctx: AutoResponseContext,
  sessionId: string,
  agentType: string,
): Promise<void> {
  const rules: AutoResponseRule[] = [];

  // Aider gitignore prompt
  if (agentType === "aider") {
    rules.push({
      pattern: /\.aider\*.*\.gitignore.*\(Y\)es\/\(N\)o/i,
      type: "config",
      response: "y",
      description: "Auto-accept adding .aider* to .gitignore",
      safe: true,
    });
  }

  // Gemini — auth flow (update notices are informational, don't need a response)
  if (agentType === "gemini") {
    // Auth menu detection — select API key or Google login based on available credentials
    const geminiApiKey = ctx.runtime.getSetting("GENERATIVE_AI_API_KEY") as
      | string
      | undefined;

    if (geminiApiKey) {
      // Have API key → select option 2 "Use an API key"
      rules.push({
        pattern:
          /Log in with Google|Use an API key|Use Vertex AI|gemini api key/i,
        type: "config",
        response: "2",
        description: "Select 'Use an API key' from Gemini auth menu",
        safe: true,
      });

      // Step 2: API key input prompt — send the actual key value.
      // Tight regex: only matches the Gemini CLI's exact prompt format
      // to prevent exfiltration via crafted terminal output.
      // once: fire at most once per session to prevent repeated credential injection.
      rules.push({
        pattern:
          /^(?:\s|[>$#])*(?:Enter|Paste) (?:your )?(?:Google AI|Gemini) API key:/i,
        type: "config",
        response: geminiApiKey,
        description: "Input Gemini API key from Gemini CLI auth prompt",
        safe: true,
        once: true,
      });
    } else {
      // No API key → select option 1 "Log in with Google" (opens browser OAuth)
      rules.push({
        pattern:
          /Log in with Google|Use an API key|Use Vertex AI|gemini api key/i,
        type: "config",
        response: "1",
        description:
          "Select 'Log in with Google' from Gemini auth menu (browser OAuth)",
        safe: true,
      });
    }
  }

  if (rules.length === 0) return;

  // Push rules to the session via the runtime API
  try {
    if (ctx.usingBunWorker) {
      for (const rule of rules) {
        await (ctx.manager as BunCompatiblePTYManager).addAutoResponseRule(
          sessionId,
          rule,
        );
      }
    } else {
      const nodeManager = ctx.manager as PTYManager;
      for (const rule of rules) {
        nodeManager.addAutoResponseRule(sessionId, rule);
      }
    }
    ctx.log(
      `Pushed ${rules.length} auto-response rules to session ${sessionId}`,
    );

    // Note: No retroactive check needed here. The worker's tryAutoResponse()
    // runs on every data chunk and checks the full output buffer against all
    // active rules. Once rules are pushed, the next data chunk will trigger
    // matching. The old retroactive check caused ghost responses because it
    // bypassed the worker's TUI-aware response logic (sendKeys vs writeRaw).
  } catch (err) {
    ctx.log(`Failed to push rules to session ${sessionId}: ${err}`);
  }
}

/**
 * Handle Gemini authentication when login_required fires.
 * Sends /auth to start the auth flow — auto-response rules
 * then handle menu selection and API key input.
 */
export async function handleGeminiAuth(
  ctx: AutoResponseContext,
  sessionId: string,
  sendKeysToSession: (
    sessionId: string,
    keys: string | string[],
  ) => Promise<void>,
): Promise<void> {
  const apiKey = ctx.runtime.getSetting("GENERATIVE_AI_API_KEY") as
    | string
    | undefined;

  if (apiKey) {
    ctx.log(
      `Gemini auth: API key available, sending /auth to start API key flow`,
    );
  } else {
    ctx.log(
      `Gemini auth: no API key configured, sending /auth for Google OAuth flow`,
    );
  }

  // Send /auth via sendKeys to avoid send() which sets status to "busy".
  // We need to stay in "authenticating" so detectReady fires after auth completes.
  try {
    await sendKeysToSession(sessionId, "/auth");
    await new Promise((r) => setTimeout(r, 50));
    await sendKeysToSession(sessionId, "enter");
  } catch (err) {
    ctx.log(`Gemini auth: failed to send /auth: ${err}`);
  }
}
